import {MAX_MEDIA_BYTES,LEGACY_MAX_MEDIA_BYTES,mediaLimitLabel} from './resumableUpload.js';
import {resolveApiBase} from './transcriptionApi.js';
import {stageCloudResponse,trustedBrowserCloudUrl} from './cloudImportTransfer.js';

export const cloudImportConfig={
 googleClientId:import.meta.env.VITE_GOOGLE_DRIVE_CLIENT_ID||'',
 googleApiKey:import.meta.env.VITE_GOOGLE_PICKER_API_KEY||'',
 googleProjectNumber:import.meta.env.VITE_GOOGLE_CLOUD_PROJECT_NUMBER||'',
 dropboxAppKey:import.meta.env.VITE_DROPBOX_CHOOSER_APP_KEY||''
};
export const googleConfigured=()=>Boolean(cloudImportConfig.googleClientId
 &&cloudImportConfig.googleApiKey&&cloudImportConfig.googleProjectNumber);
export const dropboxConfigured=()=>Boolean(cloudImportConfig.dropboxAppKey);

const scripts=new Map();
export function loadCloudScript(src,attributes={}){
 if(scripts.has(src))return scripts.get(src);
 const promise=new Promise((resolve,reject)=>{
  const existing=[...document.scripts].find(s=>s.src===src);
  const el=existing||document.createElement('script');
  if(!existing){
   el.src=src;el.async=true;
   for(const [key,value] of Object.entries(attributes))el.setAttribute(key,value);
   document.head.appendChild(el);
  }
  if(el.dataset.loaded==='true'){resolve();return;}
  el.addEventListener('load',()=>{el.dataset.loaded='true';resolve();},{once:true});
  el.addEventListener('error',()=>reject(new Error('Could not load the cloud provider picker.')), {once:true});
 });
 scripts.set(src,promise);
 promise.catch(()=>scripts.delete(src));
 return promise;
}
export const safeVideoName=name=>typeof name==='string'&&name.length>0&&name.length<=180
 &&!name.includes('/')&&!name.includes(String.fromCharCode(92))
 &&![...name].some(character=>character.charCodeAt(0)<32)
 &&/\.(mp4|webm)$/i.test(name);
const maxDirect=MAX_MEDIA_BYTES,maxRelay=LEGACY_MAX_MEDIA_BYTES;
export const cloudImportLimits={direct:maxDirect,relay:maxRelay};
const directDownloadUrl=(provider,raw)=>{
 const url=trustedBrowserCloudUrl(provider,raw);
 if(provider==='dropbox'&&(url.hostname==='www.dropbox.com'||url.hostname==='dropbox.com')){
  url.searchParams.delete('raw');url.searchParams.set('dl','1');
 }
 return url;
};
const directFetch=(url,options)=>fetch(url,{credentials:'omit',cache:'no-store',...options});

/**
 * Direct HTTPS transfers can reach 5 GB and are written to browser disk.
 * If a provider blocks cross-origin reading, a strictly allowlisted, signed-in
 * Render relay is available up to 50 MB; never route a multi-GB file via Render.
 */
export async function stageCloudLink({provider,url,filename,accessToken,onProgress,signal,
 fetchImpl=directFetch,relayFetch=fetch}){
 if(!safeVideoName(filename))throw new Error('Use an original MP4 or WebM filename (for example interview.mp4).');
 if(provider==='google'){
  if(!/^[a-zA-Z0-9_-]{10,200}$/.test(url))
   throw new Error('Google Drive returned an invalid file ID.');
  if(!accessToken)throw new Error('Sign in to Google Drive again.');
  // Only Google's API receives the scoped Drive OAuth token.
  let response;
  try{
   response=await fetchImpl('https://www.googleapis.com/drive/v3/files/'+encodeURIComponent(url)+'?alt=media',{
    headers:{Authorization:'Bearer '+accessToken},signal
   });
  }catch(error){
   if(signal?.aborted)throw error;
   throw new Error('Google Drive could not stream the selected video to your browser. Download an MP4/WebM copy and use From computer.');
  }
  if(!response.ok){
   const message=await response.json().catch(()=>({}));
   throw new Error(message.error?.message||'Google Drive download failed (HTTP '+response.status+').');
  }
  onProgress?.({phase:'downloading',route:'direct',percent:0});
  return stageCloudResponse(response,filename,{signal,onProgress});
 }
 if(!['dropbox','frameio','google-link'].includes(provider))
  throw new Error('Choose Google Drive, Dropbox or Frame.io.');
 const directUrl=directDownloadUrl(provider,url);
 let response;
 try{
  response=await fetchImpl(directUrl.href,{signal});
 }catch(error){
  if(signal?.aborted)throw error;
  // Only a network/CORS error may fall back to the server-side allowlist.
  if(!(error instanceof TypeError))throw error;
 }
 if(response){
  onProgress?.({phase:'downloading',route:'direct',percent:0});
  // No fallback for HTTP errors, HTML previews or an invalid video.
  return stageCloudResponse(response,filename,{signal,onProgress});
 }
 onProgress?.({phase:'connecting',route:'relay',percent:null});
 const token=await accessToken();
 if(!token)throw new Error('Sign in to Takefold before importing a shared link.');
 const relay=await relayFetch(resolveApiBase()+'/api/cloud-import/download',{
  method:'POST',headers:{'Content-Type':'application/json',Authorization:'Bearer '+token},
  signal,body:JSON.stringify({provider,url,filename}),cache:'no-store'
 });
 if(!relay.ok){
  const result=await relay.json().catch(()=>({}));
  const reason=result.error||'Secure cloud-link download failed (HTTP '+relay.status+').';
  if(/exceed|limit|too large/i.test(reason))
   throw new Error('The provider blocked direct browser transfer, and secure link fallback supports only '+
    mediaLimitLabel(maxRelay)+'. Choose a smaller review copy or import through a native picker.');
  throw new Error(reason);
 }
 onProgress?.({phase:'downloading',route:'relay',percent:0});
 try{
  return await stageCloudResponse(relay,filename,{signal,onProgress,maxBytes:maxRelay});
 }catch(error){
  if(/50 MB|limit|too large|exceeds/i.test(error.message))
   throw new Error('The cloud provider blocked direct downloading, and the secure fallback is limited to '+mediaLimitLabel(maxRelay)+'. Choose a smaller review proxy or a native provider picker.');
  throw error;
 }
}
export async function prepareGooglePicker(){
 if(!googleConfigured())throw new Error('Google Drive Picker needs its Google Cloud client ID, API key and project number configured.');
 await Promise.all([
  loadCloudScript('https://apis.google.com/js/api.js'),
  loadCloudScript('https://accounts.google.com/gsi/client')
 ]);
 await new Promise((resolve,reject)=>{
  window.gapi.load('picker',{callback:resolve,onerror:()=>reject(new Error('Google Picker could not initialize.'))});
 });
}
export function chooseGoogleVideo({onChosen,onError}){
 // Must be called as a direct user gesture; GIS handles Google's consent.
 const google=window.google;
 if(!google?.accounts?.oauth2||!google.picker)throw new Error('Google Picker is not ready.');
 const picker=google.picker;
 const tokenClient=google.accounts.oauth2.initTokenClient({
  client_id:cloudImportConfig.googleClientId,
  scope:'https://www.googleapis.com/auth/drive.file',
  callback:result=>{
   if(result.error||!result.access_token){
    onError?.(new Error('Google Drive access was not granted.'));return;
   }
   const token=result.access_token;
   const view=new picker.DocsView(picker.ViewId.DOCS);
   view.setMimeTypes('video/mp4,video/webm');
   const modal=new picker.PickerBuilder().setAppId(cloudImportConfig.googleProjectNumber)
    .setDeveloperKey(cloudImportConfig.googleApiKey).setOAuthToken(token)
    .addView(view).setCallback(data=>{
     if(data.action!==picker.Action.PICKED)return;
     const selections=(data.docs||[]).filter(file=>safeVideoName(file.name));
     if(!selections.length){onError?.(new Error('Choose an MP4 or WebM interview video.'));return;}
     onChosen(selections.map(file=>({provider:'google',url:file.id,filename:file.name,
      accessToken:token,bytes:file.sizeBytes||null})));
    }).build();
   modal.setVisible(true);
  },
  error_callback:error=>onError?.(new Error(error?.message||'Could not open Google sign-in.'))
 });
 tokenClient.requestAccessToken({prompt:'consent'});
}
export async function prepareDropboxChooser(){
 if(!dropboxConfigured())throw new Error('Dropbox Chooser needs a Dropbox app key configured.');
 await loadCloudScript('https://www.dropbox.com/static/api/2/dropins.js',
  {'id':'dropboxjs','data-app-key':cloudImportConfig.dropboxAppKey});
 if(!window.Dropbox?.choose)throw new Error('Dropbox Chooser is not available.');
}
export function chooseDropboxVideo({onChosen}){
 if(!window.Dropbox?.choose)throw new Error('Dropbox Chooser is not ready.');
 window.Dropbox.choose({
  linkType:'direct',multiselect:true,extensions:['.mp4','.webm'],
  sizeLimit:MAX_MEDIA_BYTES,folderselect:false,
  success:files=>onChosen(files.map(file=>({provider:'dropbox',
   url:file.link,filename:file.name,bytes:file.bytes})))
 });
}
